import { HttpClient } from "../../http/HttpClient";
import ProspectsRepository from "@/core/customers/repositories/ProspectsRepository";
import {
  ProspectsResponse,
  ProspectSearchFilters,
  ProspectDetailsResponse,
  Prospect,
} from "@/core/customers/entities/ProspectsTypes";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import { ApiResponse } from "../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../http/services/handleApiResponse/handleApiResponse";
import {
  executeRequest,
  executeVoidRequest,
} from "../../http/services/executeRequest/executeRequest";
import {
  generateProspectCredentialsUrl,
  getProspectsUrl,
  getProspectUrl,
  revokeProspectCredentialsUrl,
  submitProspectOnboardingUrl,
} from "../../api/prospectsBackendEndpoints/prospectsBackendEndpoints";
import handleRequestError from "../../http/services/handleRequestError/handleRequestError";
import {
  ProspectsPipelineMapper,
  ProspectsPipelineMapperDetails,
  ApiProspectItem,
  CustomerDemand,
  ApiProspectDetailsItem,
} from "@/core/customers/mappers/ProspectsPipelineMapper";
import { CustomerSubmissionResponse } from "@/core/customers/entities/CustomerSubmission";
import {
  CustomerSubmissionResponseDto,
  mapSubmissionDtoToEntity,
} from "./submissionMappers";

export default class ProspectsRepositoryBackend implements ProspectsRepository {
  constructor(private readonly httpClient: HttpClient) {}

  private isCustomerDemandPayload(
    payload: CustomerDemand | ApiProspectDetailsItem,
  ): payload is CustomerDemand {
    return (
      typeof payload === "object" &&
      payload !== null &&
      "customer" in payload &&
      "demand" in payload
    );
  }

  async getProspectDetails(
    id: string,
  ): Promise<RepositoryResult<ProspectDetailsResponse>> {
    try {
      const url = getProspectUrl(id);

      const response = await this.httpClient.get<
        ApiResponse<CustomerDemand | ApiProspectDetailsItem>
      >({
        url,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse<
        CustomerDemand | ApiProspectDetailsItem
      >(response);

      if (result.success) {
        const detailsPayload = result.data;
        let mappedData = null as unknown as ProspectDetailsResponse["data"];

        if (detailsPayload) {
          if (this.isCustomerDemandPayload(detailsPayload)) {
            mappedData =
              ProspectsPipelineMapperDetails.toDomainFromDemand(detailsPayload);
          } else {
            mappedData =
              ProspectsPipelineMapperDetails.toDomainFromProspectDetails(
                detailsPayload,
              );
          }
        }

        const mapped: ProspectDetailsResponse = {
          code: response.code,
          message: response.message,
          data: mappedData,
        };
        return RepositoryResultFactory.success(mapped);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  async getProspectByBusinessKey(
    businessKey: string,
  ): Promise<RepositoryResult<Prospect>> {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<ApiProspectDetailsItem>>({
          url: getProspectUrl(businessKey),
          withAuth: true,
        }),
      (data) => ProspectsPipelineMapper.toDomainFromBusinessKey(data),
    );
  }

  async getProspects(
    filters: ProspectSearchFilters,
  ): Promise<RepositoryResult<ProspectsResponse>> {
    try {
      const url = getProspectsUrl({
        filterValue: filters.name,
        customerType: filters.type,
        status: filters.status,
        channel: filters.canal,
        createdFrom: filters.createdFrom,
        createdTo: filters.createdTo,
        offset: filters.page,
        rows: filters.size,
        sortBy: filters.sortBy,
        sortDirection: filters.sortDirection,
      });

      const response = await this.httpClient.get<
        ApiResponse<{ items: ApiProspectItem[]; totalElements: number }>
      >({
        url,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse<{
        items: ApiProspectItem[];
        totalElements: number;
      }>(response);

      if (result.success) {
        // Ensure totalElements is properly converted to a number
        // Handle cases where it might be a string or undefined
        const rawTotalElements = result.data?.totalElements;
        let totalElements = 0;
        if (typeof rawTotalElements === "number") {
          totalElements = rawTotalElements;
        } else if (typeof rawTotalElements === "string") {
          totalElements = Number.parseInt(rawTotalElements, 10) || 0;
        }

        const mapped: ProspectsResponse = {
          code: response.code,
          message: response.message,
          data: result.data
            ? {
                items: ProspectsPipelineMapper.toDomainItems(
                  result.data.items || [],
                ),
                totalElements,
              }
            : { items: [], totalElements: 0 },
        };
        return RepositoryResultFactory.success(mapped);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  async submitProspectOnboarding(
    businessKey: string,
  ): Promise<RepositoryResult<CustomerSubmissionResponse>> {
    return executeRequest(
      () =>
        this.httpClient.patch<ApiResponse<CustomerSubmissionResponseDto>>({
          url: submitProspectOnboardingUrl(businessKey),
          withAuth: true,
        }),
      mapSubmissionDtoToEntity,
    );
  }

  async generateProspectCredentials(
    businessKey: string,
  ): Promise<RepositoryResult<void>> {
    return executeVoidRequest(() =>
      this.httpClient.post<ApiResponse<unknown>, { businessKey: string }>({
        url: generateProspectCredentialsUrl(),
        body: { businessKey },
        withAuth: true,
      }),
    );
  }

  async revokeProspectCredentials(
    businessKey: string,
    motif: string,
  ): Promise<RepositoryResult<void>> {
    return executeVoidRequest(() =>
      this.httpClient.post<ApiResponse<unknown>, { motif: string }>({
        url: revokeProspectCredentialsUrl(businessKey),
        body: { motif },
        withAuth: true,
      }),
    );
  }
}
